import { useState, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState, Pagination } from "@/shared/components";
import { Button, Input, ModalWrapper, Prompt, PromptForm } from "@/shared/ui";
import { useLazyMount } from "@/shared/hooks";
import {
  useHiringListPage,
  useHiringModals,
  useHiringMutations,
  useHiringDownloads,
  useHiringActions,
  type GetApplicationsResponse,
} from "@/features/hr/hiring";
import { CreateInvitationForm, HiringCardsSkeleton, HiringFilter, HiringHeader, HiringPageSkeleton, ShowApplicationModalWindow } from "./components";

const PdfViewer = lazy(() => import("@/shared/ui/PdfViewer"));

const HiringTableView = lazy(() => import("./components/HiringTableView").then((m) => ({ default: m.default })));
const HiringCardsView = lazy(() => import("./components/HiringCardsView").then((m) => ({ default: m.default })));

export default function HiringPage() {
  const [inviteOpen, setInviteOpen] = useState(false);
  const inviteWasOpened = useLazyMount(inviteOpen);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState<number | null>(null);

  const pageData = useHiringListPage();

  const modals = useHiringModals();
  const mutations = useHiringMutations(modals.setPrompt, pageData.isFetching);
  const downloads = useHiringDownloads(modals.setPrompt);
  const actions = useHiringActions(mutations, modals);

  const { t } = useTranslation("HiringPage");

  if (pageData.isLoading && !pageData.data) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1 min-w-0">
          <HiringHeader onOpenInvite={() => setInviteOpen(true)} />
          <HiringPageSkeleton />
        </section>
      </>
    );
  }

  if (pageData.isError) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1 min-w-0">
          <HiringHeader onOpenInvite={() => setInviteOpen(true)} />
          <p className="mt-4 text-body-regular-md content-action-negative">{t("messages.error")}</p>
        </section>
      </>
    );
  }

  return (
    <>
      <title>{t("meta.title")}</title>

      {inviteWasOpened && (
        <CreateInvitationForm
          hidden={!inviteOpen}
          onClose={() => setInviteOpen(false)}
          onSuccess={() => {
            setInviteOpen(false);
            setInviteSuccess(true);
          }}
        />
      )}

      {inviteSuccess && (
        <Prompt
          title={t("invite.successTitle")}
          text={t("invite.successText")}
          onClose={() => setInviteSuccess(false)}
        />
      )}

      {downloads.downloadingId && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("messages.loader.waitingForDownload.text")}
          additionalText={t("messages.loader.waitingForDownload.additionalText")}
          namespace="HiringPage"
        />
      )}

      {modals.prompt && (
        <Prompt
          title={modals.prompt.title}
          text={modals.prompt.text}
          variant={modals.prompt.variant || "success"}
          onClose={() => modals.setPrompt(null)}
        />
      )}

      {modals.approveConfirmId !== null && (
        <PromptForm
          title={t("confirm.approveTitle")}
          text={t("confirm.approveText")}
          variant="success"
          onClose={() => modals.setApproveConfirmId(null)}
          onConfirm={() => {
            const id = modals.approveConfirmId!;
            modals.setApproveConfirmId(null);
            mutations.handleApprove(id);
          }}
          isLoading={mutations.isLoading.isReviewing}
          namespace="HiringPage"
        />
      )}

      {downloads.previewUrl && (
        <Suspense fallback={null}>
          <PdfViewer
            fileUrl={downloads.previewUrl}
            fileName={downloads.previewFileName || undefined}
            onClose={downloads.closePreview}
          />
        </Suspense>
      )}

      {modals.revisionModal && (
        <ModalWrapper onClose={() => modals.setRevisionModal(null)}>
          <div className="flex flex-col gap-4">
            <h3 className="text-heading-lg content-base-primary">{t("modals.revisionTitle")}</h3>
            <div className="flex flex-col gap-2">
              <Input
                isTextarea
                placeholder={t("modals.revisionPlaceholder")}
                value={modals.revisionModal.notes}
                onChange={(e) => modals.setRevisionModal({ ...modals.revisionModal!, notes: e.target.value })}
              />
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                disabled={mutations.reviewingApplicationId === modals.revisionModal.id}
                onClick={() => {
                  if (modals.revisionModal) {
                    mutations.handleSendRevision(modals.revisionModal.id, modals.revisionModal.notes);
                    modals.setRevisionModal(null);
                  }
                }}>
                {mutations.reviewingApplicationId === modals.revisionModal.id ? t("modals.sending") : t("modals.send")}
              </Button>
            </div>
          </div>
        </ModalWrapper>
      )}

      {modals.rejectModal && (
        <ModalWrapper onClose={() => modals.setRejectModal(null)}>
          <div className="flex flex-col gap-4">
            <h3 className="text-heading-lg content-base-primary">{t("modals.rejectTitle")}</h3>
            <div className="flex flex-col gap-2">
              <Input
                isTextarea
                placeholder={t("modals.rejectPlaceholder")}
                value={modals.rejectModal.reason}
                onChange={(e) => modals.setRejectModal({ ...modals.rejectModal!, reason: e.target.value })}
              />
              <Button
                variant="destructive"
                className="w-full"
                disabled={mutations.rejectingApplicationId === modals.rejectModal.id}
                onClick={() => {
                  if (modals.rejectModal) {
                    mutations.handleSendReject(modals.rejectModal.id, modals.rejectModal.reason);
                    modals.setRejectModal(null);
                  }
                }}>
                {mutations.rejectingApplicationId === modals.rejectModal.id
                  ? t("modals.rejecting")
                  : t("actions.reject")}
              </Button>
            </div>
          </div>
        </ModalWrapper>
      )}

      {(mutations.reviewingApplicationId !== null || mutations.rejectingApplicationId !== null) && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("messages.loader.sending.text")}
          additionalText={t("messages.loader.sending.additionalText")}
          namespace="HiringPage"
        />
      )}

      {showApplicationModal !== null && pageData.data && (() => {
        const selectedApplication = pageData.data.find((app: GetApplicationsResponse) => app.id === showApplicationModal);
        if (!selectedApplication) return null;
        return (
          <ShowApplicationModalWindow
            id={showApplicationModal}
            onClose={() => setShowApplicationModal(null)}
            jobPosition={selectedApplication.job_position || ""}
            status={selectedApplication.status}
            has_contract={selectedApplication.has_contract}
            onApprove={(id) => actions.handleAction("approve", id, () => setShowApplicationModal(null))}
            onRevision={(id) => {
              setShowApplicationModal(null);
              modals.setRevisionModal({ id, notes: "" });
            }}
            onReject={(id) => {
              setShowApplicationModal(null);
              modals.setRejectModal({ id, reason: "" });
            }}
            onCreateContract={(id) => actions.handleAction("create_contract", id)}
            onDownloadProfile={downloads.handleDownloadProfile}
            isDownloading={downloads.isDownloading}
            isReviewing={mutations.reviewingApplicationId === showApplicationModal}
            isRejecting={mutations.rejectingApplicationId === showApplicationModal}
            isCreatingContract={false}
          />
        );
      })()}

      <section className="flex-1 min-w-0">
        <HiringHeader onOpenInvite={() => setInviteOpen(true)} />

        <div className="radius-3xl surface-base-fill p-7">
          <HiringFilter
            search={pageData.search}
            onSearchChange={pageData.setSearch}
            range={pageData.dateRange}
            onRangeChange={pageData.setDateRange}
            statusFilter={pageData.statusFilter}
            onStatusChange={pageData.setStatusFilter}
            statusOptions={pageData.statusOptions}
            hasActiveFilters={pageData.activeFilters}
            onReset={pageData.handleResetFilters}
            locale={pageData.locale}
            view={pageData.view}
            onViewChange={pageData.setView}
          />

          {pageData.filteredApplications.length === 0 ? (
            <EmptyState
              hasActiveFilters={pageData.activeFilters}
              noResultsText={t("table.noResults")}
              noDataText={t("table.noData")}
              noDataHint={t("table.noDataHint")}
            />
          ) : (
            <>
              {pageData.view === "table" ? (
                <Suspense fallback={<HiringPageSkeleton variant="tableOnly" />}>
                  <HiringTableView
                    applications={pageData.pageApplications}
                    sortConfig={pageData.sortConfig}
                    onSort={pageData.handleSort}
                    onOpenApplication={setShowApplicationModal}
                    actions={actions}
                    mutations={mutations}
                    downloads={{
                      onDownloadProfile: downloads.handleDownloadProfile,
                      isDownloading: downloads.isDownloading,
                    }}
                  />
                </Suspense>
              ) : (
                <Suspense fallback={<HiringCardsSkeleton />}>
                  <HiringCardsView
                    applications={pageData.pageApplications}
                    onOpenApplication={setShowApplicationModal}
                    actions={actions}
                    mutations={mutations}
                  />
                </Suspense>
              )}

              <Pagination
                currentPage={pageData.pagination.page}
                totalPages={pageData.pagination.totalPages}
                onPageChange={pageData.pagination.setPage}
                fromItem={pageData.pagination.fromItem}
                toItem={pageData.pagination.toItem}
                total={pageData.filteredApplications.length}
              />
            </>
          )}
        </div>
      </section>
    </>
  );
}
